/**
 * Tenant API service.
 *
 * Fetches the user's tenant memberships from shared-auth
 * (GET /auth/v1/tenants), including display names and slugs.
 */

import axios from 'axios';
import { AUTH_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: AUTH_API_URL });

/**
 * A binding as the editors WRITE it (shared-auth step 7, ADR-061). `scopes`
 * is the grant; `preset` is a display label that, when sent, must equal the
 * catalogue entry's scopes exactly (400 invalid_preset otherwise — a label
 * that disagreed with its scopes could only mislead a UI). '*' is a SPACE
 * (the open baseline), never a scope. The server caps every binding to the
 * grantor's own scopes in that space (403 binding_exceeds_grantor, naming
 * the missing ones).
 */
export interface TenantBinding {
  space: string;
  scopes: string[];
  preset?: string;
  expiresAt?: string;
}

/**
 * A binding as the server RETURNS it. Rows written before step 7 may still
 * carry the legacy `persona` key beside — or instead of — `scopes` until
 * PR 6 drops it, so readers accept both; a separate type keeps the write
 * paths honest — echoing a view back into a write is a type error, not a
 * silent guess (`toWriteBinding` in lib/grants is the one door).
 */
export interface TenantBindingView {
  space: string;
  persona?: string;
  preset?: string;
  scopes?: string[];
  expiresAt?: string;
}

/** 'guest' = external collaborator: viewer rank, space-exact bindings only,
 *  no rank fallback and no open-space wildcard (enterprise-authz #696). */
export type MemberClass = 'member' | 'guest';

/**
 * One entry of the preset catalogue (GET /presets): a named scope bundle a
 * picker offers. Not authority — a binding stores the scopes it names and
 * the resolver never reads the label — but the ONLY source of labels and
 * scopes in this app: there is no hand-maintained mirror (ADR-061 step 7).
 */
export interface Preset {
  slug: string;
  label: string;
  description: string;
  scopes: string[];
}

export interface TenantMembership {
  tenantId: string;
  role: string;
  memberClass?: MemberClass;
  active: boolean;
  displayName?: string;
  slug?: string;
  /** (space, scopes) bindings backing `grants`; for the members admin UI. */
  bindings?: TenantBindingView[];
  /** Resolved per-space content scopes ('*' = open-space baseline). Absent
   *  until shared-auth returns it — treat absence as ungated (fail open). */
  grants?: Record<string, string[]>;
}

export const tenantApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  /** List the tenants the current user belongs to, flagging the active one. */
  async listTenants(): Promise<TenantMembership[]> {
    const { data } = await api.get('/tenants');
    return data.tenants ?? [];
  },

  /**
   * The preset catalogue (GET /presets; any authenticated principal). The
   * ONLY source of preset labels and scopes — the app carries no mirror, so
   * a response without the list throws rather than yielding an empty picker.
   */
  async listPresets(): Promise<Preset[]> {
    const { data } = await api.get('/presets');
    if (!Array.isArray(data?.presets)) {
      throw new Error('The presets catalogue came back without a presets list.');
    }
    return data.presets;
  },

  /**
   * Store the user's active-tenant preference. The new tenant takes
   * effect on the next token refresh (Pre Token Generation reads the
   * stored preference), so callers must force-refresh the session after.
   */
  async switchTenant(tenantId: string): Promise<void> {
    await api.post('/tenant/switch', { tenantId });
  },

  /** Rename a workspace and/or change its slug (owner/admin only). */
  async updateTenant(
    tenantId: string,
    changes: { displayName?: string; slug?: string },
  ): Promise<void> {
    await api.patch(`/tenants/${tenantId}`, changes);
  },

  /**
   * Leave a workspace (remove own membership). Server-side guard
   * returns 409 when the caller is the last owner (shared-auth#121).
   */
  async leaveTenant(tenantId: string, userId: string): Promise<void> {
    await api.delete(`/tenants/${tenantId}/members/${userId}`);
  },

  /** Public slug availability check (no auth required). */
  async checkSlug(slug: string): Promise<{ available: boolean; reason?: string }> {
    const { data } = await api.get(`/slugs/${encodeURIComponent(slug)}/available`);
    return { available: Boolean(data.available), reason: data.reason };
  },

  /**
   * Create a team (org_) workspace. The server makes the caller owner
   * and flips their active-tenant preference, so callers must
   * force-refresh the session before acting in the new workspace.
   */
  async createTenant(input: {
    displayName: string;
    slug?: string;
  }): Promise<{ tenantId: string; displayName: string; slug?: string; role: string }> {
    const { data } = await api.post('/tenants', input);
    return data;
  },

  /** Invite teammates into a workspace (owner/admin only). Each invite may
   *  carry content bindings (capped server-side to the inviter's own grants);
   *  omitted bindings default to product-owner on the open baseline. Guest
   *  invites (memberClass: 'guest') must be viewer-ranked with explicit
   *  space-exact bindings — the server rejects anything else. */
  async createInvites(
    tenantId: string,
    invites: {
      email: string;
      role: 'admin' | 'editor' | 'viewer';
      bindings?: TenantBinding[];
      memberClass?: MemberClass;
    }[],
  ): Promise<void> {
    await api.post(`/tenants/${tenantId}/invites`, { invites });
  },

  /** List members of a workspace (any member). */
  async listMembers(tenantId: string): Promise<TenantMember[]> {
    const { data } = await api.get(`/tenants/${tenantId}/members`);
    return data.members ?? [];
  },

  /** Change a member's role (admin+; owner role only touchable by owners). */
  async changeMemberRole(tenantId: string, userId: string, role: string): Promise<void> {
    await api.patch(`/tenants/${tenantId}/members/${userId}`, { role });
  },

  /** Replace a member's content bindings (admin+; each binding is capped
   *  server-side to the caller's own per-space grants). */
  async setMemberBindings(
    tenantId: string,
    userId: string,
    bindings: TenantBinding[],
  ): Promise<void> {
    await api.put(`/tenants/${tenantId}/members/${userId}/bindings`, { bindings });
  },

  /** Remove a member (admin+; server 409s on the last owner). */
  async removeMember(tenantId: string, userId: string): Promise<void> {
    await api.delete(`/tenants/${tenantId}/members/${userId}`);
  },

  /** Entitlement recertification snapshot (admin+): every member's rank,
   *  class, bindings and the resolved grants their tokens carry — the
   *  exportable audit for access reviews. Generating one is itself audited
   *  server-side (authz.recert_generated). */
  async getRecertSnapshot(tenantId: string): Promise<RecertSnapshot> {
    const { data } = await api.get(`/tenants/${tenantId}/recert`);
    return data;
  },

  /** List pending invites (owner/admin only). */
  async listInvites(tenantId: string): Promise<PendingInvite[]> {
    const { data } = await api.get(`/tenants/${tenantId}/invites`);
    return data.invites ?? [];
  },

  /** Revoke a pending invite (owner/admin only). */
  async revokeInvite(tenantId: string, inviteId: string): Promise<void> {
    await api.delete(`/tenants/${tenantId}/invites/${inviteId}`);
  },

  /** Redeem an invite token for a membership (shared-auth#120). */
  async acceptInvite(
    token: string,
  ): Promise<{ tenantId: string; role: string; displayName?: string }> {
    const { data } = await api.post('/invites/accept', { token });
    return data;
  },

  // ── Native groups (workspace-organization groups-model.md) ─────────

  /** List the workspace's groups (any member). */
  async listGroups(tenantId: string): Promise<TenantGroup[]> {
    const { data } = await api.get(`/tenants/${tenantId}/groups`);
    return data.groups ?? [];
  },

  /** Group detail including member ids (any member). */
  async getGroup(tenantId: string, groupId: string): Promise<TenantGroupDetail> {
    const { data } = await api.get(`/tenants/${tenantId}/groups/${groupId}`);
    return data;
  },

  /** Create a group (admin+; name unique per workspace). */
  async createGroup(
    tenantId: string,
    input: { name: string; description?: string },
  ): Promise<TenantGroup> {
    const { data } = await api.post(`/tenants/${tenantId}/groups`, input);
    return data;
  },

  /** Rename / re-describe a group (admin+). */
  async updateGroup(
    tenantId: string,
    groupId: string,
    input: { name?: string; description?: string },
  ): Promise<TenantGroup> {
    const { data } = await api.patch(`/tenants/${tenantId}/groups/${groupId}`, input);
    return data;
  },

  /** Delete a group; the server strips it from every member (admin+). */
  async deleteGroup(tenantId: string, groupId: string): Promise<void> {
    await api.delete(`/tenants/${tenantId}/groups/${groupId}`);
  },

  /** Replace a group's bindings (admin+; capped server-side to the caller's
   *  own per-space grants — no owner break-glass on the group path). */
  async setGroupBindings(
    tenantId: string,
    groupId: string,
    bindings: TenantBinding[],
  ): Promise<TenantGroup> {
    const { data } = await api.put(`/tenants/${tenantId}/groups/${groupId}/bindings`, { bindings });
    return data;
  },

  /** Add a member to a group (admin+; the server rejects guests). */
  async addGroupMember(tenantId: string, groupId: string, userId: string): Promise<void> {
    await api.put(`/tenants/${tenantId}/groups/${groupId}/members/${userId}`);
  },

  /** Remove a member from a group (admin+). */
  async removeGroupMember(tenantId: string, groupId: string, userId: string): Promise<void> {
    await api.delete(`/tenants/${tenantId}/groups/${groupId}/members/${userId}`);
  },
};

export interface TenantGroup {
  groupId: string;
  name: string;
  description: string;
  bindings: TenantBindingView[];
  /** Resolved per-space scopes the group's bindings confer. */
  grants: Record<string, string[]>;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface TenantGroupDetail extends TenantGroup {
  members: string[];
}

export interface TenantMember {
  userId: string;
  email: string;
  name: string;
  role: string;
  memberClass?: MemberClass;
  joinedAt: string;
  /** MANUAL-plane (space, scopes) bindings only — group-derived access is
   *  never folded in here, so binding editors cannot clobber it. Nothing is
   *  synthesized: what is shown is what is stored (step 7 removed the rank
   *  fallback), which is what tokens carry. */
  bindings?: TenantBindingView[];
  /** Resolved EFFECTIVE per-space scopes (manual ∪ groups; '*' = baseline). */
  grants?: Record<string, string[]>;
  /** Native-group provenance: group ids and what they confer. */
  groups?: string[];
  groupGrants?: Record<string, string[]>;
}

export interface PendingInvite {
  inviteId: string;
  email: string;
  role: string;
  memberClass?: MemberClass;
  bindings?: TenantBindingView[];
  invitedBy: string;
  createdAt: string;
  expiresAt: number;
}

export interface RecertSnapshot {
  tenantId: string;
  generatedAt: string;
  generatedBy: string;
  memberCount: number;
  members: (TenantMember & {
    identitySource?: string;
    resolvedGrants?: string;
    grantsSource?: 'materialized' | 'resolved';
    grantsVer?: number;
  })[];
}

/** Extract the human-readable detail from an RFC 7807 problem response. */
export function problemDetail(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: { detail?: string } } }).response?.data;
    if (data?.detail) return data.detail;
  }
  return fallback;
}

/** The stable `code` of an RFC 7807 problem response (e.g. `step_up_required`), or null. */
export function problemCode(err: unknown): string | null {
  if (err && typeof err === 'object' && 'response' in err) {
    const code = (err as { response?: { data?: { code?: unknown } } }).response?.data?.code;
    if (typeof code === 'string' && code) return code;
  }
  return null;
}

/** HTTP status of an axios error, or 0 when unavailable. */
export function errorStatus(err: unknown): number {
  if (err && typeof err === 'object' && 'response' in err) {
    return (err as { response?: { status?: number } }).response?.status ?? 0;
  }
  return 0;
}
